import { useEffect, useMemo, useState, useCallback } from 'react';
import type { RefObject } from 'react';
import type { DepositIncomingCard } from '@/supabase/deposit/types/deposit';

/**
 * 입금 칩이 화면에 안 보이는 매칭 행을 추적해 '점프 배너'로 surface(놓침 방지).
 * 두 경우를 합집합으로 센다:
 *  (a) 검색-가림: 매칭 orderId가 filteredCustomers에 없음(렌더 안 됨).
 *  (b) 스크롤-오프: 렌더됐지만 스크롤 컨테이너 뷰포트 밖(IntersectionObserver).
 *
 * 무회귀/perf 주의(codexloop P2 R1):
 *  - setState는 변화 없으면 prev 그대로 반환(no-op 렌더 churn·렌더↔effect 피드백 루프 차단).
 *  - stale oid(확정/제거)는 effect 재실행 시 가지치기 + count에서도 이중 방어.
 *  - filteredCustomers는 호출부에서 useMemo로 안정화돼야 effect가 매 렌더 재실행되지 않는다.
 */
export function useDepositJump(
  depositByOrderId: Map<number, DepositIncomingCard>,
  filteredCustomers: ReadonlyArray<{ id: string }>,
  listContainerRef: RefObject<HTMLElement>,
  searchQuery: string,
  setSearchQuery: (q: string) => void,
) {
  const filteredOrderIds = useMemo(() => {
    const s = new Set<number>();
    for (const c of filteredCustomers) {
      const mm = c.id.match(/^order_(\d+)$/);
      if (mm) s.add(Number(mm[1]));
    }
    return s;
  }, [filteredCustomers]);

  const [offscreenOrderIds, setOffscreenOrderIds] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    const root = listContainerRef.current;
    if (!root || depositByOrderId.size === 0) {
      setOffscreenOrderIds((prev) => (prev.size === 0 ? prev : new Set()));
      return;
    }
    // 더 이상 매칭 아닌 oid(확정/제거) 가지치기 — 변화 없으면 prev 유지(루프 방지).
    setOffscreenOrderIds((prev) => {
      let changed = false;
      const next = new Set<number>();
      prev.forEach((oid) => { if (depositByOrderId.has(oid)) next.add(oid); else changed = true; });
      return changed ? next : prev;
    });
    const io = new IntersectionObserver((entries) => {
      setOffscreenOrderIds((prev) => {
        let changed = false;
        const next = new Set(prev);
        for (const e of entries) {
          const mm = ((e.target as HTMLElement).dataset.customerId ?? '').match(/^order_(\d+)$/);
          if (!mm) continue;
          const oid = Number(mm[1]);
          if (e.isIntersecting) { if (next.delete(oid)) changed = true; }
          else if (!next.has(oid)) { next.add(oid); changed = true; }
        }
        return changed ? next : prev; // no-op 콜백은 렌더 유발 안 함.
      });
    }, { root, threshold: 0 });
    depositByOrderId.forEach((_c, oid) => {
      const el = root.querySelector(`[data-customer-id="order_${oid}"]`);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [depositByOrderId, filteredCustomers, listContainerRef]);

  // 안 보이는 매칭 orderId(검색가림 ∪ 스크롤오프). 둘은 서로소(가림=렌더 안 됨).
  const hiddenOrderIds = useMemo(() => {
    const ids: number[] = [];
    depositByOrderId.forEach((_c, oid) => {
      if (!filteredOrderIds.has(oid)) ids.push(oid);          // 검색가림
      else if (offscreenOrderIds.has(oid)) ids.push(oid);     // 스크롤오프
    });
    return ids;
  }, [depositByOrderId, filteredOrderIds, offscreenOrderIds]);

  const jumpToHiddenDeposit = useCallback(() => {
    const targetOid = hiddenOrderIds[0]; // 안 보이는 매칭 중 맵 순서상 첫 건(보이는 행으로 점프하던 버그 해소; 시각적 최상단 보장은 아님).
    if (targetOid == null) return;
    if (searchQuery) setSearchQuery(''); // 검색가림이면 해제해 그 행을 마운트.
    setTimeout(() => {
      const root = listContainerRef.current;
      const el = root?.querySelector(`[data-customer-id="order_${targetOid}"]`) as HTMLElement | null;
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }, [hiddenOrderIds, searchQuery, setSearchQuery, listContainerRef]);

  return { hiddenDepositCount: hiddenOrderIds.length, jumpToHiddenDeposit };
}
